'use client';

import { useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import ErrorAlert from '@/components/ui/ErrorAlert';
import TickAnalysisTable from '@/components/tick-analysis/TickAnalysisTable';
import { useFilteredTickSnapshots } from '@/hooks/useFilteredTickSnapshots';
import BackButton from '@/components/ui/BackButton';
import { Activity, RefreshCw, Info } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import AuthGuard from '@/components/ui/AuthGuard';

// Filter type definitions - labels with trading hints
const FILTER_LABELS = {
  "only_buyers": "Only Buyers - Stocks you can buy for small run",
  "only_sellers": "Only Sellers - Avoid or short opportunity",
  "buyer_dominated": "Buyer Dominated - Strong momentum for intraday",
  "seller_dominated": "Seller Dominated - High risk, consider exit",
  "volume_breakout": "Volume Breakout - Potential big move ahead",
  "price_surge": "Rally Up Stocks - Trending stocks to ride",
  "price_drop": "Price Drop - Falling knives or buy dip",
  "near_52w_high": "Near 52W High - Breakout candidates",
  "near_52w_low": "Near 52W Low - Value picks or avoid traps",
};

// Detailed descriptions for info tooltips
const FILTER_DESCRIPTIONS = {
  "only_buyers": "Upper circuit stocks with only buyers - total buy quantity > 0, total sell quantity = 0, buy value > ₹10K. Perfect for catching small intraday runs when circuit opens.",
  "only_sellers": "Lower circuit stocks with only sellers - total sell quantity > 0, total buy quantity = 0, sell value > ₹10K. Avoid buying or consider shorting opportunities.",
  "buyer_dominated": "Strong buying pressure - buy quantity ≥ 3× sell quantity (both non-zero). Great for intraday momentum trades with strong buyer interest.",
  "seller_dominated": "Strong selling pressure - sell quantity ≥ 3× buy quantity (both non-zero). High risk zone - consider exiting positions or shorting.",
  "volume_breakout": "Volume breakout - today's volume > 2× one-month average volume. Indicates institutional interest and potential for significant price movement.",
  "price_surge": "Price surge - price up more than 5% from previous close. Trending stocks with momentum - good for swing trades.",
  "price_drop": "Price drop - price down more than 5% from previous close. Either falling knives to avoid or potential dip buying opportunities.",
  "near_52w_high": "Near 52-week high - stock trading within 5% of its 52-week high. Potential breakout candidates showing strength.",
  "near_52w_low": "Near 52-week low - stock down more than 40% from its 52-week high. Value opportunities or value traps - research carefully.",
};

const DEFAULT_FILTER_TYPE = "only_buyers";

export default function FilteredTickAnalysisPage() {
  const [selectedFilterType, setSelectedFilterType] = useState<string>(DEFAULT_FILTER_TYPE);
  const [hasFetched, setHasFetched] = useState(false);
  
  const { 
    snapshots, 
    loading, 
    error, 
    fetchFilteredTickSnapshots 
  } = useFilteredTickSnapshots();

  const handleFetchData = async () => {
    setHasFetched(true);
    await fetchFilteredTickSnapshots(selectedFilterType);
  };

  return (
    <PageLayout>
      <AuthGuard>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <header className="mb-6">
            <div className="flex items-center gap-3">
              <BackButton />
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">Smart Stock Screener</h1>
            </div>
            <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400">
              Buying pressure, circuits, volume breakouts, price surges and 52-week levels
            </p>
          </header>

          {/* Filter Type Section */}
          <Card className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <div className="flex items-center gap-2 mb-2">
                  <label htmlFor="filter-type" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Insight Filter Type
                  </label>
                  <div className="group relative">
                    <Info className="h-4 w-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 cursor-help" />
                    <div className="absolute left-0 top-6 w-72 p-3 bg-zinc-900 dark:bg-zinc-800 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                      <div className="font-semibold mb-1">{FILTER_LABELS[selectedFilterType as keyof typeof FILTER_LABELS]}</div>
                      <div className="text-zinc-300">{FILTER_DESCRIPTIONS[selectedFilterType as keyof typeof FILTER_DESCRIPTIONS]}</div>
                      <div className="absolute -top-1 left-4 w-2 h-2 bg-zinc-900 dark:bg-zinc-800 rotate-45"></div>
                    </div>
                  </div>
                </div>
                <select
                  id="filter-type"
                  value={selectedFilterType}
                  onChange={(e) => setSelectedFilterType(e.target.value)}
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                >
                  {Object.entries(FILTER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <Button onClick={handleFetchData} disabled={loading} className="gap-2 px-6">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : hasFetched ? 'Refresh Data' : 'Fetch Data'}
              </Button>
            </div>
          </Card>
          
          {error && <ErrorAlert message={error} className="mb-6" />}
          
          <main>
            {loading ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-zinc-200 bg-white py-16 dark:border-zinc-800 dark:bg-zinc-900">
                <Spinner size="lg" className="text-emerald-600 dark:text-emerald-400" />
                <span className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">Loading filtered tick snapshot data...</span>
              </div>
            ) : hasFetched && snapshots.length > 0 ? (
              <TickAnalysisTable snapshots={snapshots} />
            ) : hasFetched && snapshots.length === 0 ? (
              <Card className="py-16 text-center">
                <Activity className="mx-auto h-10 w-10 text-zinc-300 dark:text-zinc-600" />
                <h3 className="mt-4 text-base font-semibold text-zinc-900 dark:text-white">No Data Available</h3>
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
                  No tick snapshot data matches the selected filter criteria. This could be because the market is closed or no stocks match the filter conditions.
                </p>
                <Button variant="outline" onClick={handleFetchData} className="mt-6 gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
              </Card>
            ) : null}
          </main>
        </div>
      </AuthGuard>
    </PageLayout>
  );
}
